import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Trash2, Edit, Save, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface TarotEducationContent {
  id?: string;
  content_type: string;
  title: string;
  content: string;
  key_value?: string;
  sort_order: number;
  is_published: boolean;
}

export default function TarotEducationManager() {
  const [contents, setContents] = useState<TarotEducationContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingContent, setEditingContent] = useState<TarotEducationContent | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadContents();
  }, []);

  const loadContents = async () => {
    const { data, error } = await supabase
      .from('tarot_education')
      .select('*')
      .order('sort_order');

    if (error) {
      toast({ title: 'Error loading content', description: error.message, variant: 'destructive' });
    } else {
      setContents(data || []);
    }
    setLoading(false);
  };

  const saveContent = async (content: TarotEducationContent) => {
    if (content.id) {
      const { error } = await supabase
        .from('tarot_education')
        .update(content)
        .eq('id', content.id);

      if (error) {
        toast({ title: 'Error updating content', description: error.message, variant: 'destructive' });
      } else {
        toast({ title: 'Content updated successfully' });
        loadContents();
        setEditingContent(null);
      }
    } else {
      const { error } = await supabase
        .from('tarot_education')
        .insert([content]);

      if (error) {
        toast({ title: 'Error creating content', description: error.message, variant: 'destructive' });
      } else {
        toast({ title: 'Content created successfully' });
        loadContents();
        setIsCreating(false);
      }
    }
  };

  const deleteContent = async (id: string) => {
    if (!confirm('Are you sure you want to delete this content?')) return;

    const { error } = await supabase
      .from('tarot_education')
      .delete()
      .eq('id', id);

    if (error) {
      toast({ title: 'Error deleting content', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Content deleted successfully' });
      loadContents();
    }
  };

  const ContentForm = ({ content, onSave, onCancel }: { 
    content: TarotEducationContent; 
    onSave: (content: TarotEducationContent) => void; 
    onCancel: () => void 
  }) => {
    const [formData, setFormData] = useState<TarotEducationContent>(content);

    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{content.id ? 'Edit Content' : 'New Content'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Content Type</Label>
            <Select 
              value={formData.content_type} 
              onValueChange={(value) => setFormData({ ...formData, content_type: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="introduction">Introduction</SelectItem>
                <SelectItem value="suit">Suit</SelectItem>
                <SelectItem value="number">Number</SelectItem>
                <SelectItem value="psychological">Psychological</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Title</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., 'What is Tarot?' or 'Cups - The Emotional Realm'"
            />
          </div>

          <div>
            <Label>Key Value (optional)</Label>
            <Input
              value={formData.key_value || ''}
              onChange={(e) => setFormData({ ...formData, key_value: e.target.value })}
              placeholder="e.g., 'cups', 'swords', '1', '2' for filtering"
            />
            <p className="text-xs text-muted-foreground mt-1">
              For suits: use lowercase suit name. For numbers: use the number (1-10)
            </p>
          </div>

          <div>
            <Label>Content</Label>
            <Textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              rows={12}
              placeholder="Write detailed explanation..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Sort Order</Label>
              <Input
                type="number"
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_published}
                onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
              />
              <Label>Published</Label>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={() => onSave(formData)} className="gap-2">
              <Save className="h-4 w-4" />
              Save
            </Button>
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return <div className="text-center py-8">Loading education content...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Tarot Education Content Manager</CardTitle>
          <CardDescription>
            Manage educational content about tarot suits, numbers, psychology, and introductory material
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={() => setIsCreating(true)}
            disabled={isCreating || editingContent !== null}
          >
            Add New Content
          </Button>
        </CardContent>
      </Card>

      {isCreating && (
        <ContentForm
          content={{
            content_type: 'introduction',
            title: '',
            content: '',
            sort_order: 0,
            is_published: true
          }}
          onSave={saveContent}
          onCancel={() => setIsCreating(false)}
        />
      )}

      {editingContent && (
        <ContentForm
          content={editingContent}
          onSave={saveContent}
          onCancel={() => setEditingContent(null)}
        />
      )}

      <div className="space-y-4">
        {contents.map((content) => (
          <Card key={content.id}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-lg">{content.title}</CardTitle>
                  <div className="flex gap-2">
                    <Badge variant="secondary">{content.content_type}</Badge>
                    {content.key_value && <Badge variant="outline">{content.key_value}</Badge>}
                    {!content.is_published && <Badge variant="destructive">Unpublished</Badge>}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setEditingContent(content)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => content.id && deleteContent(content.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground line-clamp-3 whitespace-pre-wrap">
                {content.content}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Sort order: {content.sort_order}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
